// VENDORED COPY of frontend/src/lib/financial/constants.ts — do not edit here; keep in sync with the main app.
/**
 * Financial calculation constants and interval configuration.
 *
 * Ported from static/apps/lib/financial-calc.js to eliminate the
 * window.AppLib dependency. All values are exact mirrors of the legacy
 * INTERVAL / INTERVAL_MAP objects.
 */

import type { IntervalConfig, IntervalKey } from './types';

/**
 * Interval definitions with months-per-period and periods-per-year.
 * Matches the RECURRING_INTERVAL constants used by products-cost.js.
 */
export const INTERVAL: Record<IntervalKey, IntervalConfig> = {
  monthly:    { months: 1,  label: 'Monthly',     periodsPerYear: 12 },
  bimonthly:  { months: 2,  label: 'Bi-Monthly',  periodsPerYear: 6 },
  quarterly:  { months: 3,  label: 'Quarterly',   periodsPerYear: 4 },
  halfyearly: { months: 6,  label: 'Half-Yearly', periodsPerYear: 2 },
  yearly:     { months: 12, label: 'Yearly',      periodsPerYear: 1 },
} as const;

/**
 * Map numeric interval IDs (from the cost/savings API) to string keys.
 */
export const INTERVAL_MAP: Record<number, IntervalKey> = {
  1: 'monthly',
  2: 'bimonthly',
  3: 'quarterly',
  4: 'halfyearly',
  5: 'yearly',
} as const;

/**
 * Resolve an interval config from either a string key or a numeric API value.
 * Falls back to monthly if the input is unrecognised.
 *
 * @param interval - Interval key string or numeric ID from the API
 * @returns The resolved IntervalConfig
 */
export function getIntervalConfig(interval: string | number | undefined | null): IntervalConfig {
  if (typeof interval === 'number') {
    const key = INTERVAL_MAP[interval] ?? 'monthly';
    return INTERVAL[key];
  }
  if (typeof interval === 'string' && interval in INTERVAL) {
    return INTERVAL[interval as IntervalKey];
  }
  return INTERVAL.monthly;
}
